import { override } from 'mobx';

import { moment } from '@feathr/hooks';
import type { TConstraints, TOptionalConstraint } from '@feathr/rachis';

import { TRIGGERED_EMAIL_DEFAULT_DELAY } from '../constants';
import { triggerableMailingConstraints } from './mailing';
import { MailrailBaseEmailCampaign } from './mailrail_email_base';
import type {
  IEmailTrigger,
  IMailrailAutoSendEmailCampaign,
  IStoredEmailTrigger,
  TSelectableAutosendSubtype,
} from './types';
import { CampaignState } from './types';

type PartialAttrs = Partial<IMailrailAutoSendEmailCampaign>;

/**
 * Returns the predicate kind used by the specified Autosend campaign subtype.
 *
 * @param subtype - The campaign's trigger type.
 * @returns The kind the campaign's trigger predicates must have. Date triggers read existing
 *   person attributes, so their kind is `attribute`.
 */
export function getAutosendTriggerKind(
  subtype: TSelectableAutosendSubtype,
): IStoredEmailTrigger['kind'] {
  return subtype === 'time' ? 'attribute' : subtype;
}

export class MailrailAutoSendEmailCampaign<
  T extends IMailrailAutoSendEmailCampaign = IMailrailAutoSendEmailCampaign,
> extends MailrailBaseEmailCampaign<T> {
  public override get constraints(): TConstraints<T> {
    return {
      ...super.constraints,
      ...triggerableMailingConstraints,
      bulk_send_at_end: {
        exclusion: (_: boolean, attributes: PartialAttrs): TOptionalConstraint => {
          if (attributes.subtype === 'time') {
            return {
              within: [true],
              message: '^A date-triggered campaign cannot be combined with a bulk send',
            };
          }
          if (attributes.repeat_allowed) {
            return {
              within: [true],
              message:
                '^Post-campaign bulk send is incompatible with the "Send every time" setting',
            };
          }
          return undefined;
        },
      },
      bulk_send_at_start: {
        exclusion: (_: boolean, attributes: PartialAttrs): TOptionalConstraint => {
          // A started campaign's start bulk send has already run and can no longer change
          if (attributes.subtype === 'time' && !this.hasStarted) {
            return {
              within: [true],
              message: '^A date-triggered campaign cannot be combined with a bulk send',
            };
          }
          return undefined;
        },
      },
      date_end: {
        presence: {
          allowEmpty: false,
          message: "^End date can't be blank",
        },
        datetime: (_: string, attributes: PartialAttrs): TOptionalConstraint => {
          const startPlusOneDay = moment.utc(attributes.date_start).add(1, 'day').startOf('minute');
          const nowPlusFifteenMinutes = moment.utc().add(15, 'minutes').startOf('minute');
          const minEndDate = moment.max(startPlusOneDay, nowPlusFifteenMinutes);
          return {
            earliest: minEndDate,
            message: `^End time must be at least 24 hours after start time and at least 15 minutes from now`,
          };
        },
      },
      date_start: {
        // Time subtype autosends require a date_start because its time component is used as the daily send time
        presenceUnless: {
          allowEmpty: false,
          message: "^Time of day can't be blank",
          unless: ({ subtype }) => subtype !== 'time',
        },
        datetime: (value: string, attributes: PartialAttrs): TOptionalConstraint => {
          if (!value) {
            return undefined;
          }
          if (attributes.state === CampaignState.Draft) {
            return {
              earliest: moment.utc().add(15, 'minutes').startOf('minute'),
              message: `^Start time must be at least 15 minutes in the future`,
            };
          }
          return undefined;
        },
      },
      segments_included: {
        length: (_: string[], attributes: PartialAttrs): TOptionalConstraint => {
          if (attributes.bulk_send_at_end) {
            return {
              minimum: 1,
              tooShort: '^A post-campaign bulk send requires at least one included group',
            };
          }
          return undefined;
        },
        list: {
          presence: {
            allowEmpty: false,
            message: '^Finish selecting a group or remove the placeholder group.',
          },
        },
        duplicates: {
          maximum: 0,
          message: '^Groups must be unique.',
        },
      },
      predicates(value: IEmailTrigger[]): TOptionalConstraint {
        if (value.length < 1) {
          return {
            length: { minimum: 1, message: '^You must have at least one trigger' },
          };
        }

        // The comparisons "exists" and "not exists" do not require a value.
        const filteredPredicates = value.filter(
          ({ comparison }) => !(['exists', 'nexists'] as unknown[]).includes(comparison),
        );

        // True and false are valid values, so we only want to check for undefined, empty string, and null.
        if (
          filteredPredicates.some(({ value }) =>
            (['', undefined, null] as unknown[]).includes(value),
          )
        ) {
          return {
            array: {
              value: {
                presence: { allowEmpty: false, message: '^Trigger value cannot be blank' },
              },
            },
          };
        }

        return undefined;
      },
      repeat_allowed: {
        exclusion: (_: boolean, attributes: PartialAttrs): TOptionalConstraint => {
          if (attributes.bulk_send_at_end) {
            return {
              within: [true],
              message: '^Repeat sending is not compatible with the post-campaign bulk send option',
            };
          }
          return undefined;
        },
        presence: {
          allowEmpty: false,
          message:
            "^Choose whether you'd like your email to be sent only once or each time the action is performed",
        },
      },
      repeat_cooldown_seconds: {
        presenceUnless: {
          allowEmpty: false,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          unless: ({ repeat_allowed }) => repeat_allowed === false,
        },
        numericality: {
          onlyInteger: true,
          greaterThanOrEqualTo: 0,
          message: '^Cooldown period must be a non-negative number',
        },
      },
      subtype: {
        exclusion: {
          within: ['mixed'],
          message: '^Choose a trigger type before publishing',
        },
      },
    };
  }

  @override
  public override get isEditable(): boolean {
    if (this.isPublishing || this.isCompleting || this.isErroring) {
      return false;
    }
    // If a campaign executed a post-campaign bulk send, it should not be editable or re-publishable.
    // A post-campaign bulk send can only be added when the campaign is in draft or scheduled states,
    // so it shouldn't be possible for users to inadvertently trigger this rule by editing a completed
    // campaign.
    if (this.isComplete && this.get('bulk_send_at_end')) {
      return false;
    }
    return true;
  }

  public override getDefaults(): Partial<T> {
    return {
      ...super.getDefaults(),
      bulk_send_at_end: false,
      bulk_send_at_start: false,
      delay_seconds: TRIGGERED_EMAIL_DEFAULT_DELAY,
      predicates: [],
      predicates_mode: 'match_all',
      repeat_allowed: false,
      subtype: 'activity',
    };
  }
}
